/**
 * Web-Crypto-compatible base64url encoding helpers for `@shn/sdk/audit`.
 *
 * # Why we don't reuse `@shn/substrate-outbound-signing/jws-helpers`
 *
 * Substrate-side helpers at `packages/substrate-outbound-signing/src/jws-helpers.ts`
 * use Node `Buffer` (not browser-portable) and `derToP1363` / `p1363ToDer`
 * conversion (Node `crypto.verify` requires DER for EC keys). SDK-side runs
 * in browsers + Node 19+ via Web Crypto API which:
 *
 * - Has no `Buffer` (browsers); uses `Uint8Array` / `ArrayBuffer` natively
 * - Accepts ECDSA signatures in IEEE P1363 (R||S) form **directly** for
 *   `crypto.subtle.verify({name: 'ECDSA', hash: 'SHA-256'}, ...)` — no DER
 *   conversion needed (one fewer code path than Node-side helpers)
 *
 * Per `feedback_premature_extraction.md` — these helpers are SDK-internal
 * for now; if a future SDK module (e.g., `@shn/sdk/identity` Phase 5) needs
 * the same primitives, the third-consumer extraction trigger fires.
 */
/**
 * Decode a base64url string (RFC 4648 §5; no padding) to a `Uint8Array`.
 *
 * Web Crypto API + browser-compatible — uses `atob` (browser + Node 16+)
 * after transforming URL-safe base64 alphabet (`-`/`_`) back to standard
 * base64 (`+`/`/`) and re-padding. No Node `Buffer` dependency.
 *
 * @throws Error with `cause` for invalid base64url input.
 */
export function base64UrlDecode(input) {
    const standardBase64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const paddingNeeded = (4 - (standardBase64.length % 4)) % 4;
    const padded = standardBase64 + '='.repeat(paddingNeeded);
    const binary = atob(padded);
    const out = new Uint8Array(new ArrayBuffer(binary.length));
    for (let i = 0; i < binary.length; i += 1) {
        out[i] = binary.charCodeAt(i);
    }
    return out;
}
/**
 * Encode a `Uint8Array` to a base64url string (RFC 4648 §5; no padding).
 *
 * Web Crypto API + browser-compatible — uses `btoa` (browser + Node 16+)
 * after building a binary string from the bytes, then transforming
 * standard base64 alphabet to URL-safe.
 *
 * Used for canonical bytes encoding when computing the JWS signing input
 * `<protected_b64>.<canonical_b64>` (RFC 7515 §3 detached JWS form).
 */
export function base64UrlEncode(input) {
    let binary = '';
    for (let i = 0; i < input.length; i += 1) {
        binary += String.fromCharCode(input[i]);
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}
//# sourceMappingURL=jws-utils.js.map